import React, { Suspense, lazy } from "react";
import { BrowserRouter, Navigate, Routes as RouterRoutes, Route, useLocation, useNavigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import AppLoader from "components/ui/AppLoader";
import FloatingChatbot from "components/FloatingChatbot";
import { apiGetBlockedPages, apiValidateSession, getCurrentBlockedPagesScopeKey, hasCachedBlockedPagesSnapshot, readCachedBlockedPages } from "utils/authApi";
import { isNetworkUnavailable } from "utils/offlineErp";
import { hasEffectiveActiveSubscription, hasOrganizationScope, isPasswordChangeRequired, isPasswordChangeSnoozed, resolveEffectiveSubscriptionPlanCode, resolvePostLoginPath, resolveRoleHomePath, useErpContext } from "utils/erpContext";
import { useNetworkStatus } from "utils/useNetworkStatus";

const LandingSplash = lazy(() => import("./pages/landing-splash"));
const LandingPage = lazy(() => import("./pages/landing"));
const VerificarDocumentoPage = lazy(() => import("./pages/verificar-documento"));
const PublicSplashPage = lazy(() => import("./pages/splash"));
const SplashHomePage = lazy(() => import("./pages/splash-home"));
const LoginPage = lazy(() => import("./pages/login"));
const ChangePasswordPage = lazy(() => import("./pages/alterar-senha"));
const RegisterPage = lazy(() => import("./pages/register"));
const CompanySetupPage = lazy(() => import("./pages/company-setup"));
const ManagerOverview = lazy(() => import("./pages/manager-overview"));
const CeoDashboard = lazy(() => import("./pages/ceo-dashboard"));
const StoreManagerDashboard = lazy(() => import("./pages/store-manager-dashboard"));
const ExecutiveKpiDashboard = lazy(() => import("./pages/executive-kpi-dashboard"));
const FinancialComplianceHub = lazy(() => import("./pages/financial-compliance-hub"));
const TreasuryDashboardPage = lazy(() => import("./pages/treasury-dashboard"));
const AgtManagement = lazy(() => import("./pages/agt-management"));
const SalesAnalyticsDashboard = lazy(() => import("./pages/sales-analytics-dashboard"));
const PosTerminal = lazy(() => import("./pages/pos-terminal"));
const PosSaleSuccess = lazy(() => import("./pages/pos-sale-success"));
const CustomerRegistry = lazy(() => import("./pages/customer-registry"));
const ProductCatalog = lazy(() => import("./pages/product-catalog"));
const Transacoes = lazy(() => import("./pages/transacoes"));
const TransacoesAjustes = lazy(() => import("./pages/transacoes-ajustes"));
const TransacoesValidar = lazy(() => import("./pages/transacoes-validar"));
const TransacoesDocumentos = lazy(() => import("./pages/transacoes-documentos"));
const TransacoesDocumentosLista = lazy(() => import("./pages/transacoes-documentos-lista"));
const ProdutosVendidosPage = lazy(() => import("./pages/produtos-vendidos"));
const ObservabilityEventsPage = lazy(() => import("./pages/observability-events"));
const AssistantTelemetryPage = lazy(() => import("./pages/assistant-telemetry"));
const CobrancasPage = lazy(() => import("./pages/cobrancas"));
const PosDailyReportPage = lazy(() => import("./pages/pos-daily-report"));
const SubscriptionPlansPage = lazy(() => import("./pages/subscription-plans"));
const AdminSignatureLicensesPage = lazy(() => import("./pages/admin-signature-licenses"));
const AdminPanelPage = lazy(() => import("./pages/admin-painel"));
const AfiliadoDashboardPage = lazy(() => import("./pages/afiliado/dashboard"));
const AfiliadoVendasPage = lazy(() => import("./pages/afiliado/vendas"));
const AfiliadoVendaDetalhePage = lazy(() => import("./pages/afiliado/venda-detalhe"));
const AfiliadoComissoesPage = lazy(() => import("./pages/afiliado/comissoes"));
const AfiliadoCarteiraPage = lazy(() => import("./pages/afiliado/carteira"));
const AfiliadoExtratoPage = lazy(() => import("./pages/afiliado/extrato"));
const AfiliadoSolicitarSaquePage = lazy(() => import("./pages/afiliado/solicitar-saque"));
const AfiliadoSaquesPage = lazy(() => import("./pages/afiliado/saques"));
const AfiliadoSaqueDetalhePage = lazy(() => import("./pages/afiliado/saque-detalhe"));
const AfiliadoPagamentoPage = lazy(() => import("./pages/afiliado/metodos-pagamento"));
const AfiliadoPerfilPage = lazy(() => import("./pages/afiliado/perfil"));
const AccessControlPage = lazy(() => import("./pages/access-control"));
const SettingsPage = lazy(() => import("./pages/settings"));
const ProfileSettingsPage = lazy(() => import("./pages/profile-settings"));
const AjudaSuportePage = lazy(() => import("./pages/ajuda-suporte"));
const VendorManagementPage = lazy(() => import("./pages/vendor-management"));
const NotFound = lazy(() => import("pages/NotFound"));
const MaintenancePage = lazy(() => import("pages/maintenance"));
const SubscriptionLockedPage = lazy(() => import("pages/subscription-locked"));
const PaymentRestrictedPage = lazy(() => import("pages/payment-restricted"));

// "/" incluída de propósito: o próprio index.html da raiz já tem
// title/description/JSON-LD bons, servidos estaticamente — sem isto,
// RouteSeoGuard (abaixo) reescrevia essas tags para noindex,nofollow
// assim que o JS corria (a Google renderiza a página antes de indexar),
// desfazendo silenciosamente o que o robots.txt/HTML estático já
// permitiam.
const INDEXABLE_PUBLIC_PATHS = new Set(["/", "/landing"]);

// Rotas de apenas consulta/leitura que continuam acessíveis mesmo com a
// assinatura da empresa/loja inactiva — histórico de vendas, facturas já
// emitidas, relatórios e dashboards. A empresa pode precisar destes dados
// para responder a uma auditoria AGT independentemente do estado do
// pagamento, por isso não ficam reféns da assinatura em atraso. Tudo o que
// NÃO estiver nesta lista (e não for a própria página de planos/licenças)
// fica bloqueado por uma tela dedicada — ver SubscriptionLockedPage — em vez
// do antigo redireccionamento total para /subscription-plans.
const SUBSCRIPTION_READ_ONLY_ROUTES = [
  "/manager-overview",
  "/ceo-dashboard",
  "/store-manager-dashboard",
  "/executive-kpi-dashboard",
  "/financial-compliance-hub",
  "/treasury-dashboard",
  "/sales-analytics-dashboard",
  "/agt-management",
  "/customer-registry",
  "/product-catalog",
  "/transacoes",
  "/transacoes/documentos",
  "/transacoes/documentos-lista",
  "/produtos-vendidos",
  "/pos-relatorio-diario",
  "/profile-settings",
  "/ajuda-suporte",
];

function isSubscriptionReadOnlyRoute(pathname) {
  const path = String(pathname || "");
  return SUBSCRIPTION_READ_ONLY_ROUTES.some((route) => path === route || path.startsWith(`${route}/`));
}

function RouteSeoGuard() {
  const location = useLocation();

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const path = location?.pathname || "/";
    const shouldIndex = INDEXABLE_PUBLIC_PATHS.has(path);
    const robotsContent = shouldIndex
      ? "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"
      : "noindex,nofollow,max-image-preview:none,max-snippet:0,max-video-preview:0";

    ["robots", "googlebot", "bingbot"].forEach((name) => {
      let tag = document.head.querySelector(`meta[name="${name}"]`);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute("name", name);
        document.head.appendChild(tag);
      }

      tag.setAttribute("content", robotsContent);
    });
  }, [location?.pathname]);

  return null;
}

function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: "var(--color-background)" }}>
      <AppLoader title="Aguarde..." message="A preparar o próximo módulo com segurança." compact />
    </div>
  );
}

// ── Maintenance pages cache ─────────────────────────────────────────────────
// Papéis que podem ver páginas em manutenção sem serem bloqueados
const MAINTENANCE_BYPASS_ROLES = ["executive", "admin"];

// Cache em módulo para não refetch a cada remount
let _blockedPagesCache = readCachedBlockedPages();
let _blockedPagesCacheAt = _blockedPagesCache?.length ? Date.now() : 0;
let _blockedPagesTrusted = hasCachedBlockedPagesSnapshot();
let _blockedPagesScopeKey = getCurrentBlockedPagesScopeKey();
const BLOCKED_PAGES_TTL_MS = 2 * 60 * 1000; // 2 min

async function fetchBlockedPages(scopeKey = getCurrentBlockedPagesScopeKey()) {
  const now = Date.now();
  if (_blockedPagesScopeKey !== scopeKey) {
    _blockedPagesCache = readCachedBlockedPages();
    _blockedPagesCacheAt = _blockedPagesCache?.length ? Date.now() : 0;
    _blockedPagesTrusted = hasCachedBlockedPagesSnapshot();
    _blockedPagesScopeKey = scopeKey;
  }

  if (_blockedPagesCache !== null && now - _blockedPagesCacheAt < BLOCKED_PAGES_TTL_MS) {
    return {
      pages: _blockedPagesCache,
      trusted: _blockedPagesTrusted,
    };
  }

  const result = await apiGetBlockedPages({ includeMeta: true });
  const pages = Array.isArray(result?.items) ? result.items : [];

  _blockedPagesCache = pages;
  _blockedPagesCacheAt = now;
  _blockedPagesTrusted = Boolean(result?.trusted);
  _blockedPagesScopeKey = scopeKey;

  return {
    pages,
    trusted: _blockedPagesTrusted,
  };
}

function useBlockedPages(scopeKey) {
  const [blockedPagesState, setBlockedPagesState] = React.useState(() => ({
    pages: Array.isArray(_blockedPagesCache) ? _blockedPagesCache : readCachedBlockedPages(),
    trusted: _blockedPagesTrusted,
    ready: false,
  }));

  React.useEffect(() => {
    let active = true;
    fetchBlockedPages(scopeKey).then((result) => {
      if (!active) return;

      setBlockedPagesState({
        pages: Array.isArray(result?.pages) ? result.pages : [],
        trusted: Boolean(result?.trusted),
        ready: true,
      });
    });
    return () => { active = false; };
  }, [scopeKey]);

  return blockedPagesState;
}

const SPLASH_EXIT_MS = 680;
const SPLASH_INACTIVITY_MS = 30 * 60 * 1000;
const AUTH_HEARTBEAT_MS = 60 * 1000;
const ERP_SESSION_EXPIRED_EVENT = "angolabi:erp-session-expired";

function isOfflineSessionToken(token) {
  return String(token || "").trim() === "offline-session";
}
const SPLASH_STORAGE_KEYS = {
  lastActivityAt: "angolabi.splash.lastActivityAt",
  lastDismissedAt: "angolabi.splash.lastDismissedAt",
  forceShow: "angolabi.splash.forceShow"
};

function readSplashTimestamp(key) {
  if (typeof window === "undefined") return 0;

  const rawValue = Number(window.localStorage.getItem(key));
  return Number.isFinite(rawValue) ? rawValue : 0;
}

function writeSplashTimestamp(key, value) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(key, String(value));
}

function shouldShowStartupSplash() {
  if (typeof window === "undefined") return true;

  const now = Date.now();
  const forceShow = window.localStorage.getItem(SPLASH_STORAGE_KEYS.forceShow) === "1";
  const lastDismissedAt = readSplashTimestamp(SPLASH_STORAGE_KEYS.lastDismissedAt);
  const lastActivityAt = readSplashTimestamp(SPLASH_STORAGE_KEYS.lastActivityAt);

  if (forceShow) return true;
  if (!lastDismissedAt) return true;

  const referenceTimestamp = Math.max(lastDismissedAt, lastActivityAt);
  return now - referenceTimestamp >= SPLASH_INACTIVITY_MS;
}

function StartupSplashGate() {
  const location = useLocation();
  const { isAuthenticated } = useErpContext();
  const [visible, setVisible] = React.useState(shouldShowStartupSplash);
  const [exiting, setExiting] = React.useState(false);
  const initialPathRef = React.useRef(location?.pathname);
  const exitTimerRef = React.useRef(null);
  const inactivityTimerRef = React.useRef(null);
  const lastActivityWriteRef = React.useRef(0);
  const currentPath = String(location?.pathname || "");
  const isHomeSplashPath = currentPath === "/home-splash";

  const clearInactivityTimer = React.useCallback(() => {
    if (inactivityTimerRef.current) {
      window.clearTimeout(inactivityTimerRef.current);
      inactivityTimerRef.current = null;
    }
  }, []);

  const armInactivityTimer = React.useCallback(() => {
    if (typeof window === "undefined") return;

    clearInactivityTimer();
    inactivityTimerRef.current = window.setTimeout(() => {
      setExiting(false);
      setVisible(true);
    }, SPLASH_INACTIVITY_MS);
  }, [clearInactivityTimer]);

  const registerActivity = React.useCallback(() => {
    const now = Date.now();

    if (now - lastActivityWriteRef.current >= 15000) {
      writeSplashTimestamp(SPLASH_STORAGE_KEYS.lastActivityAt, now);
      lastActivityWriteRef.current = now;
    }
  }, []);

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    if (!isAuthenticated || location?.pathname === "/login" || isHomeSplashPath) {
      setExiting(false);
      setVisible(false);
      return;
    }

    if (shouldShowStartupSplash()) {
      setExiting(false);
      setVisible(true);
    }
  }, [isAuthenticated, isHomeSplashPath, location?.pathname]);

  const beginDismiss = React.useCallback(() => {
    setExiting((current) => {
      if (current) return current;

      const now = Date.now();
      writeSplashTimestamp(SPLASH_STORAGE_KEYS.lastDismissedAt, now);
      writeSplashTimestamp(SPLASH_STORAGE_KEYS.lastActivityAt, now);
      window.localStorage.removeItem(SPLASH_STORAGE_KEYS.forceShow);
      lastActivityWriteRef.current = now;
      clearInactivityTimer();

      exitTimerRef.current = window.setTimeout(() => {
        setVisible(false);
      }, SPLASH_EXIT_MS);

      return true;
    });
  }, [clearInactivityTimer]);

  React.useEffect(() => {
    if (!isAuthenticated || location?.pathname === "/login" || isHomeSplashPath) return;

    if (!visible && shouldShowStartupSplash()) {
      setExiting(false);
      setVisible(true);
      return;
    }

    if (visible) {
      initialPathRef.current = location?.pathname;
    }
  }, [isHomeSplashPath, isAuthenticated, location?.pathname, visible]);

  React.useEffect(() => {
    if (!isAuthenticated || location?.pathname === "/login" || isHomeSplashPath) return;

    if (location?.pathname !== initialPathRef.current) {
      beginDismiss();
    }
  }, [beginDismiss, isAuthenticated, isHomeSplashPath, location?.pathname]);

  React.useEffect(() => {
    if (!visible || typeof window === "undefined") return undefined;

    const dismissSplash = () => beginDismiss();

    window.addEventListener("pointerdown", dismissSplash, true);
    window.addEventListener("keydown", dismissSplash, true);

    return () => {
      window.removeEventListener("pointerdown", dismissSplash, true);
      window.removeEventListener("keydown", dismissSplash, true);
    };
  }, [beginDismiss, visible]);

  React.useEffect(() => {
    if (visible || !isAuthenticated || isHomeSplashPath || typeof window === "undefined") return undefined;

    const activityEvents = ["pointerdown", "keydown", "scroll", "touchstart", "focus"];
    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivityWriteRef.current >= 15000) {
        writeSplashTimestamp(SPLASH_STORAGE_KEYS.lastActivityAt, now);
        lastActivityWriteRef.current = now;
      }
    };

    activityEvents.forEach((eventName) => window.addEventListener(eventName, handleActivity, true));
    armInactivityTimer();

    return () => {
      activityEvents.forEach((eventName) => window.removeEventListener(eventName, handleActivity, true));
      clearInactivityTimer();
    };
  }, [armInactivityTimer, clearInactivityTimer, isAuthenticated, isHomeSplashPath, visible]);

  React.useEffect(() => () => {
    if (exitTimerRef.current) {
      window.clearTimeout(exitTimerRef.current);
    }

    clearInactivityTimer();
  }, [clearInactivityTimer]);

  if (!isAuthenticated || location?.pathname === "/login" || isHomeSplashPath || !visible) return null;

  return <LandingSplash isExiting={exiting} />;
}

function AuthSessionGuard() {
  const location = useLocation();
  const { isAuthenticated, authToken, currentUser, refreshAuthSession } = useErpContext();
  const refreshAuthSessionRef = React.useRef(refreshAuthSession);
  const lastHeartbeatRef = React.useRef(0);
  const heartbeatInFlightRef = React.useRef(false);
  const [autoLogoutTransition, setAutoLogoutTransition] = React.useState(false);
  const autoLogoutTimerRef = React.useRef(null);
  // Quando a sessão expira, várias chamadas em curso (heartbeat, polling
  // do badge AGT, auto-refresh do hub fiscal, etc.) podiam cada uma
  // disparar este handler em separado — cada disparo reiniciava o timer
  // de 450ms, dando o efeito visual de "vários refresh" antes do
  // redirecionamento real para /login. authApi.js já só dispara o evento
  // uma vez por sessão agora, mas mantém-se esta guarda aqui também: só a
  // primeira chamada arranca a transição, as seguintes são ignoradas.
  const autoLogoutTriggeredRef = React.useRef(false);

  React.useEffect(() => {
    refreshAuthSessionRef.current = refreshAuthSession;
  }, [refreshAuthSession]);

  const startAutoLogoutTransition = React.useCallback(() => {
    if (autoLogoutTriggeredRef.current) {
      return;
    }
    autoLogoutTriggeredRef.current = true;

    setAutoLogoutTransition(true);
    autoLogoutTimerRef.current = window.setTimeout(() => {
      setAutoLogoutTransition(false);
      autoLogoutTimerRef.current = null;
    }, 450);
  }, []);

  React.useEffect(() => () => {
    if (autoLogoutTimerRef.current) {
      window.clearTimeout(autoLogoutTimerRef.current);
      autoLogoutTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated || location?.pathname === "/login" || typeof window === "undefined") return undefined;

    const touchServerSession = async (force = false) => {
      if (authToken && isOfflineSessionToken(authToken)) {
        return;
      }

      const now = Date.now();

      if (!force && now - lastHeartbeatRef.current < AUTH_HEARTBEAT_MS) {
        return;
      }

      if (heartbeatInFlightRef.current) return;

      heartbeatInFlightRef.current = true;

      try {
        const refreshed = await refreshAuthSessionRef.current();
        if (!refreshed?.ok) {
          throw new Error(refreshed?.message || "Nao foi possivel sincronizar a sessao.");
        }
        lastHeartbeatRef.current = Date.now();
      } catch (error) {
        // Se a validação falhar, o interceptor de auth trata o 401/expiração.
        // Em falhas transitórias de rede, tentamos de novo no próximo heartbeat.
        if (!isNetworkUnavailable(error)) {
          console.debug("[AUTH] refreshAuthSession falhou; será re-tentado no próximo heartbeat.");
        }
      } finally {
        heartbeatInFlightRef.current = false;
      }
    };

    void touchServerSession(true);

    const handleVisibilityOrFocus = () => {
      void touchServerSession(true);
    };

    window.addEventListener("focus", handleVisibilityOrFocus, true);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus, true);

    const heartbeatInterval = window.setInterval(() => {
      void touchServerSession();
    }, AUTH_HEARTBEAT_MS);

    return () => {
      window.clearInterval(heartbeatInterval);
      window.removeEventListener("focus", handleVisibilityOrFocus, true);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus, true);
    };
  }, [authToken, isAuthenticated, location?.pathname]);

  React.useEffect(() => {
    if (!isAuthenticated || typeof window === "undefined") {
      return undefined;
    }

    const handleSessionExpiredUiTransition = () => {
      startAutoLogoutTransition();
    };

    window.addEventListener(ERP_SESSION_EXPIRED_EVENT, handleSessionExpiredUiTransition);

    return () => {
      window.removeEventListener(ERP_SESSION_EXPIRED_EVENT, handleSessionExpiredUiTransition);
    };
  }, [isAuthenticated, startAutoLogoutTransition]);

  return autoLogoutTransition ? (
    <div
      className="fixed inset-0 pointer-events-none"
      style={{
        zIndex: 1800,
        background: 'radial-gradient(circle at 50% 30%, rgba(255,255,255,0.35), rgba(255,255,255,0.10) 42%, rgba(11,18,32,0.30) 100%)',
        backdropFilter: 'blur(2px) saturate(1.03)',
        WebkitBackdropFilter: 'blur(2px) saturate(1.03)',
      }}
      aria-hidden="true"
    />
  ) : null;
}

function ProtectedRoute({ children }) {
  const location = useLocation();
  const { isAuthenticated, authToken, currentUser, activeCompany, activeStore, canAccessRoute, logout, refreshAuthSession } = useErpContext();
  const blockedPagesScopeKey = React.useMemo(() => {
    const userUuid = String(currentUser?.uuid || "anonymous").trim() || "anonymous";
    const companyUuid = String(currentUser?.companyUuid || activeCompany?.uuid || "global").trim() || "global";
    const storeUuid = String(currentUser?.storeUuid || activeStore?.uuid || "all").trim() || "all";
    return `${userUuid}::${companyUuid}::${storeUuid}`;
  }, [activeCompany?.uuid, activeStore?.uuid, currentUser?.companyUuid, currentUser?.storeUuid, currentUser?.uuid]);
  const blockedPagesState = useBlockedPages(blockedPagesScopeKey);
  const blockedPages = blockedPagesState?.pages || [];
  const blockedPagesTrusted = Boolean(blockedPagesState?.trusted);
  const blockedPagesReady = Boolean(blockedPagesState?.ready);
  // Reativo (eventos online/offline + verificação ativa contra a própria
  // API) em vez de ler navigator.onLine diretamente no corpo do render —
  // essa leitura direta nunca se atualizava sozinha (só mudava se algo
  // *outro* forçasse um novo render), o que deixava a app presa a acreditar
  // que continuava offline muito depois da ligação real ter voltado,
  // sobretudo no desktop Tauri (ver utils/useNetworkStatus.js).
  const isOnline = useNetworkStatus();
  const isOffline = !isOnline;
  const wasOfflineRef = React.useRef(!isOnline);
  React.useEffect(() => {
    const wasOffline = wasOfflineRef.current;
    wasOfflineRef.current = !isOnline;
    if (isOnline && wasOffline && isAuthenticated) {
      // Confirma a sessão e recarrega os dados da organização assim que a
      // ligação volta de facto — sem isto, o utilizador só via a app
      // "recuperar sozinha" se, por acaso, alguma outra ação disparasse um
      // pedido novo; nalguns casos ficava com dados desatualizados ou
      // acabava por ser pedido para autenticar de novo mais tarde, em vez
      // de a sessão já válida continuar normalmente.
      void refreshAuthSession({ forceReloadCompanies: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refreshAuthSession
    // não é memoizada em erpContext.js (nova função a cada render); incluí-la
    // reiniciaria este efeito constantemente sem mudar o comportamento real.
  }, [isOnline, isAuthenticated]);
  const isTauriDesktop = Boolean(
    typeof window !== "undefined"
    && (String(window.location?.hostname || "").toLowerCase() === "tauri.localhost"
      || typeof window.__TAURI_INTERNALS__ !== "undefined")
  );
  const normalizedRole = String(currentUser?.roleValue || "").trim().toLowerCase();
  const isAdminSuper = isAdminSuperUser(currentUser);
  const isAfiliado = isAfiliadoUser(currentUser);
  const hasCompanyScope = Boolean(String(currentUser?.companyUuid || "").trim());
  const hasStoreScope = Boolean(String(currentUser?.storeUuid || "").trim());
  const isGeneralManagerRole = normalizedRole === "gerente_geral";
  const isGeneralManagerRoleOnly = normalizedRole === "gerente_geral";
  const baseShouldForceLogoutForMissingStore = Boolean(currentUser && !hasStoreScope && !isGeneralManagerRole && !isAdminSuper && !isAfiliado);
  const isCashierRole = normalizedRole === "caixa";
  const scopedStoresCount = Array.isArray(activeCompany?.stores)
    ? activeCompany.stores.filter((store) => String(store?.id || "").trim() !== "all").length
    : 0;
  const directCompanyStoreCount = Number(currentUser?.companyStoreCount || 0);
  const companyStoreCount = Number.isFinite(scopedStoresCount) && scopedStoresCount > 0
    ? scopedStoresCount
    : (Number.isFinite(directCompanyStoreCount) && directCompanyStoreCount > 0
      ? directCompanyStoreCount
      : 0);
  const shouldUseStoreManagerDashboard = normalizedRole === "gerente_geral" && companyStoreCount === 1;
  // O utilizador pode adiar a troca obrigatória (botão X em /alterar-senha) só
  // para a sessão atual — nesse caso não forçamos o redirecionamento de volta;
  // no próximo login a exigência volta (ver isPasswordChangeSnoozed).
  const forcePasswordChange = Boolean(
    isPasswordChangeRequired(currentUser)
    && !isPasswordChangeSnoozed(currentUser?.uuid, authToken)
  );
  const logoutAttemptRef = React.useRef(false);

  const onboardingPath = "/company-setup";
  const subscriptionPath = "/subscription-plans";
  const adminLicensePath = "/admin-licencas-assinatura";
  const requiresOnboarding = Boolean(
    currentUser
    && (
      !hasCompanyScope
      || (isGeneralManagerRole && !hasStoreScope)
    )
  );
  const currentPath = String(location?.pathname || "");
  const isChangePasswordPath = currentPath === "/alterar-senha";
  const shouldForceLogoutForMissingStore = Boolean(baseShouldForceLogoutForMissingStore && !isChangePasswordPath);
  const inSubscriptionPage = currentPath === subscriptionPath;
  const inAdminLicensePage = currentPath === adminLicensePath;
  const hasActiveSubscription = activeCompany ? hasEffectiveActiveSubscription(activeCompany, activeStore) : false;
  const isReadOnlyRouteWhenInactive = isSubscriptionReadOnlyRoute(currentPath);

  React.useEffect(() => {
    if (!shouldForceLogoutForMissingStore || logoutAttemptRef.current) return;
    logoutAttemptRef.current = true;
    logout("manual", {
      skipRemote: true,
      notice: "Sem loja atribuida ao seu utilizador. Contacte o seu Gerente para associar uma loja e tente novamente.",
    });
  }, [shouldForceLogoutForMissingStore, logout]);

  React.useEffect(() => {
    logoutAttemptRef.current = false;
  }, [currentUser?.uuid]);

  // Restrição de acesso por incumprimento de pagamento (Admin > Planos):
  // refreshAuthSession() normalmente reutiliza `companies` já em memória
  // (evita flood a session_bootstrap.php a cada heartbeat de 60s — ver
  // AuthHeartbeat mais acima neste ficheiro), por isso um separador já
  // aberto não vê sozinho activeCompany.paymentRestricted mudar. Este
  // polling adicional e isolado (não mexe no heartbeat partilhado) força
  // um forceReloadCompanies periódico + no focus/visibilitychange, para o
  // ecrã de bloqueio aparecer sem precisar de logout/login manual.
  React.useEffect(() => {
    if (!isAuthenticated || typeof window === "undefined") return undefined;

    const PAYMENT_RESTRICTION_POLL_MS = 90000;
    let inFlight = false;

    const refreshCompanyRestrictionState = () => {
      if (inFlight) return;
      inFlight = true;
      Promise.resolve(refreshAuthSession({ forceReloadCompanies: true }))
        .catch(() => {
          // Falhas transitórias ficam para a próxima tentativa — o
          // heartbeat/interceptor de sessão já trata expiração real.
        })
        .finally(() => {
          inFlight = false;
        });
    };

    const handleVisibilityOrFocus = () => refreshCompanyRestrictionState();

    window.addEventListener("focus", handleVisibilityOrFocus, true);
    document.addEventListener("visibilitychange", handleVisibilityOrFocus, true);
    const pollInterval = window.setInterval(refreshCompanyRestrictionState, PAYMENT_RESTRICTION_POLL_MS);

    return () => {
      window.clearInterval(pollInterval);
      window.removeEventListener("focus", handleVisibilityOrFocus, true);
      document.removeEventListener("visibilitychange", handleVisibilityOrFocus, true);
    };
  }, [isAuthenticated, refreshAuthSession]);

  // ── Guardas síncronas (sem flash de página errada) ──────────────────────
  // Sem redireccionamento especial para a raiz ("/"): cai directo no
  // bloco de baixo, exactamente como qualquer outra rota protegida sem
  // sessão — vai sempre para /login, nunca para /landing nem /splash
  // (já esteve a ir para ambas, ver histórico). Isto é deliberado, não
  // um esquecimento: confirmado via Search Console que a Google executa
  // o JS até ao fim e avalia o estado final da página, não o HTML
  // estático inicial — por isso um redireccionamento client-side para
  // /login (correctamente noindex) fazia a raiz nunca ficar indexável,
  // independentemente do que o index.html estático dissesse. A solução se
  // ficou por index.html: rel="canonical" aponta para /landing (que não
  // redirecciona para lado nenhum), dizendo à Google para indexar essa
  // em vez de tentar (e falhar) indexar a raiz directamente. O
  // comportamento de navegação em si (para onde o utilizador é enviado)
  // e o sinal de SEO (canonical) são mecanismos separados — mudar um não
  // muda o outro.
  //
  // fromSplash:true evita que PublicLoginRoute reencaminhe para /splash —
  // quem chega aqui já estava dentro da app (não é uma visita fresca), por
  // isso não faz sentido voltar a mostrar o ecrã de marca antes do login.
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location, fromSplash: true }} />;
  }

  if (isChangePasswordPath) {
    return children;
  }

  if (forcePasswordChange) {
    return <Navigate to="/alterar-senha" replace state={{ reason: currentUser?.passwordChangeReason || "first_login" }} />;
  }

  if (isAdminSuper && currentPath !== "/admin-painel") {
    return <Navigate to="/admin-painel" replace />;
  }

  if (isAfiliado && !currentPath.startsWith("/afiliado")) {
    return <Navigate to="/afiliado" replace />;
  }

  if (shouldForceLogoutForMissingStore) {
    return <RouteFallback />;
  }

  if (requiresOnboarding && currentPath !== onboardingPath) {
    return <Navigate to={onboardingPath} replace />;
  }

  if (!requiresOnboarding && !activeCompany) {
    if (hasCompanyScope) {
      return <RouteFallback />;
    }

    return children;
  }

  // Restrição de acesso por incumprimento de compromisso de pagamento
  // (Admin > Planos) — decisão confirmada: bloqueio TOTAL, sem excepção de
  // rotas de leitura (ao contrário do gate de assinatura expirada logo
  // abaixo, que deixa SUBSCRIPTION_READ_ONLY_ROUTES acessíveis). Fica antes
  // desse gate porque um incumprimento de pagamento é mais grave do que uma
  // simples assinatura expirada.
  if (!requiresOnboarding && activeCompany?.paymentRestricted) {
    return (
      <PaymentRestrictedPage
        companyName={activeCompany?.name}
        reason={activeCompany?.paymentRestrictionReason}
        commitmentDate={activeCompany?.paymentCommitmentDate}
      />
    );
  }

  if (!requiresOnboarding && activeCompany && !hasActiveSubscription && !inSubscriptionPage && !inAdminLicensePage) {
    // Consulta/histórico continua acessível sem assinatura activa (ver
    // SUBSCRIPTION_READ_ONLY_ROUTES) — só as acções que geram valor (POS,
    // facturação, stock, cobranças, gestão de utilizadores...) ficam
    // bloqueadas, e agora com uma tela dedicada em vez de expulsar o
    // utilizador para /subscription-plans mesmo quando só queria consultar
    // algo já existente.
    if (!isReadOnlyRouteWhenInactive) {
      return (
        <SubscriptionLockedPage
          companyName={activeCompany?.name}
          priorPlanCode={resolveEffectiveSubscriptionPlanCode(activeCompany, activeStore)}
        />
      );
    }
  }

  if (isCashierRole) {
    if (currentPath === "/subscription-plans") {
      return children;
    }

    const isPosAllowed =
      currentPath === "/pos-terminal" ||
      currentPath.startsWith("/pos-terminal/") ||
      currentPath === "/cobrancas" ||
      currentPath.startsWith("/cobrancas/") ||
      currentPath === "/transacoes" ||
      currentPath.startsWith("/transacoes/") ||
      currentPath === "/produtos-vendidos" ||
      currentPath.startsWith("/produtos-vendidos/") ||
      currentPath === "/pos-relatorio-diario" ||
      currentPath.startsWith("/pos-relatorio-diario/") ||
      currentPath === "/ajuda-suporte";
    if (!isPosAllowed) {
      return <Navigate to="/pos-terminal" replace />;
    }
  }

  if (!canAccessRoute(currentPath)) {
    return <Navigate to={resolveRoleHomePath(currentUser?.roleValue, "", currentUser)} replace />;
  }

  if (!isGeneralManagerRoleOnly && currentPath === "/ceo-dashboard") {
    return <Navigate to={resolveRoleHomePath(currentUser?.roleValue, "", currentUser)} replace />;
  }

  if (shouldUseStoreManagerDashboard && currentPath === "/ceo-dashboard") {
    return <Navigate to="/store-manager-dashboard" replace />;
  }

  if (!requiresOnboarding && currentPath === onboardingPath) {
    return <Navigate to={resolveRoleHomePath(currentUser?.roleValue, "", currentUser)} replace />;
  }

  // ── Verificação de manutenção ────────────────────────────────────────────
  const canBypassMaintenance = MAINTENANCE_BYPASS_ROLES.includes(
    String(currentUser?.roleValue || "").toLowerCase()
  );

  if (!blockedPagesReady) {
    return <RouteFallback />;
  }

  const canOperateWithPermissionOnlyOffline = isOffline || isOfflineSessionToken(authToken) || isTauriDesktop;
  const effectiveBlockedPages = blockedPagesTrusted ? blockedPages : (canOperateWithPermissionOnlyOffline ? [] : blockedPages);

  if (!blockedPagesTrusted && !canOperateWithPermissionOnlyOffline) {
    // Em rotas protegidas com sessão já autenticada, não bloqueia a navegação por falta
    // temporária do snapshot de páginas. As permissões por utilizador/rota continuam ativas.
    console.warn("[NAV] Snapshot confiável de páginas indisponível; a navegação seguirá com fallback por permissões.");
  }

  if (effectiveBlockedPages.length > 0) {
    const currentPath = location?.pathname || "";
    const blocked = effectiveBlockedPages.find((p) => {
      const rota = String(p?.rota || "").trim();
      // Match exato ou prefixo de rotas dinâmicas (ex: /transacoes/ajustes/:id)
      return currentPath === rota || currentPath.startsWith(rota + "/");
    });

    if (blocked) {
      const isDisabled = Number(blocked?.ativo) === 0;
      const isUnderMaintenance = Number(blocked?.em_manutencao) === 1;

      // Página desativada → invisível para o utilizador, redireciona silenciosamente para o início
      if (isDisabled) {
        const homePath = resolveRoleHomePath(currentUser?.roleValue, "", currentUser);
        return <Navigate to={homePath} replace />;
      }

      // Página em manutenção → mostra tela de manutenção com informação
      // (papéis de bypass continuam autorizados neste cenário)
      if (isUnderMaintenance && !canBypassMaintenance) {
        return (
          <MaintenancePage
            pageName={blocked?.nome || ""}
            message={blocked?.mensagem_manutencao || ""}
            expectedReturn={blocked?.previsao_retorno || null}
          />
        );
      }
    }
  }

  return children;
}

function GuardedNotFoundRoute() {
  const { isAuthenticated, currentUser } = useErpContext();
  const normalizedRole = String(currentUser?.roleValue || "").trim().toLowerCase();
  const hasStoreScope = Boolean(String(currentUser?.storeUuid || "").trim());
  const isGeneralManagerRole = normalizedRole === "gerente_geral";
  const isAfiliado = isAfiliadoUser(currentUser);

  if (isAuthenticated && currentUser && isAfiliado) {
    return <Navigate to="/afiliado" replace />;
  }

  if (isAuthenticated && currentUser && !hasOrganizationScope(currentUser) && isGeneralManagerRole) {
    return <Navigate to="/company-setup" replace />;
  }

  if (isAuthenticated && currentUser && !hasStoreScope && !isGeneralManagerRole) {
    return <Navigate to="/login" replace state={{ fromSplash: true }} />;
  }

  return <NotFound />;
}

function PublicLoginRoute() {
  const location = useLocation();
  const { isAuthenticated, authToken, currentUser, logout } = useErpContext();
  const normalizedRole = String(currentUser?.roleValue || "").trim().toLowerCase();
  const isAdminSuper = isAdminSuperUser(currentUser);
  const isAfiliado = isAfiliadoUser(currentUser);
  const hasStoreScope = Boolean(String(currentUser?.storeUuid || "").trim());
  const isGeneralManagerRole = normalizedRole === "gerente_geral";
  const forcePasswordChange = Boolean(
    isPasswordChangeRequired(currentUser)
    && !isPasswordChangeSnoozed(currentUser?.uuid, authToken)
  );
  // Nao forca logout por falta de loja se o utilizador precisa trocar senha primeiro
  const shouldForceLogoutForMissingStore = Boolean(
    isAuthenticated && currentUser && !hasStoreScope && !isGeneralManagerRole && !isAdminSuper && !isAfiliado && !forcePasswordChange
  );
  const logoutAttemptedRef = React.useRef(false);

  React.useEffect(() => {
    if (!shouldForceLogoutForMissingStore || logoutAttemptedRef.current) return;
    logoutAttemptedRef.current = true;
    logout("manual", {
      skipRemote: true,
      notice: "Sem loja atribuida ao seu utilizador. Contacte o seu Gerente para associar uma loja e tente novamente.",
    });
  }, [logout, shouldForceLogoutForMissingStore]);

  if (shouldForceLogoutForMissingStore) {
    return <RouteFallback />;
  }

  if (isAuthenticated) {
    if (forcePasswordChange) {
      return <Navigate to="/alterar-senha" replace state={{ reason: currentUser?.passwordChangeReason || "first_login" }} />;
    }
    const nextPath = resolvePostLoginPath(currentUser, [], location?.state?.from?.pathname || "");
    return <Navigate to={nextPath.path} replace state={nextPath.state || undefined} />;
  }

  // redirectToLogin() (erpContext.js, usado nos fluxos de sessão expirada)
  // faz um location.assign() de página inteira para "/login?reason=...&
  // notice=...", em vez de um navigate() do React Router — por isso nunca
  // consegue passar state.fromSplash. Sem este bypass adicional, esses
  // casos também saltavam para /splash, e a mensagem de aviso (ex.: "Sessão
  // expirada, autentique-se novamente") nunca chegava a aparecer, porque
  // /splash não a mostra e o clique em "Continuar" também não a reencaminha.
  const hasSessionReasonParam = Boolean(new URLSearchParams(String(location?.search || "")).get("reason"));
  const cameFromPublicSplash = Boolean(location?.state?.fromSplash) || hasSessionReasonParam;
  if (!cameFromPublicSplash) {
    return <Navigate to="/splash" replace state={{ from: location }} />;
  }

  return <LoginPage />;
}

function PublicSplashRoute() {
  const location = useLocation();
  const { isAuthenticated, authToken, currentUser } = useErpContext();
  const forcePasswordChange = Boolean(
    isPasswordChangeRequired(currentUser)
    && !isPasswordChangeSnoozed(currentUser?.uuid, authToken)
  );

  if (isAuthenticated) {
    if (forcePasswordChange) {
      return <Navigate to="/alterar-senha" replace state={{ reason: currentUser?.passwordChangeReason || "first_login" }} />;
    }

    const nextPath = resolvePostLoginPath(currentUser, [], location?.state?.from?.pathname || "");
    return <Navigate to={nextPath.path} replace state={nextPath.state || undefined} />;
  }

  return <PublicSplashPage />;
}

function isAdminSuperUser(user) {
  const roleValue = String(user?.roleValue || "").trim().toLowerCase();
  const rawRoleCode = String(user?.rawRoleCode || user?.papel_codigo || "").trim().toLowerCase();
  const rawRoleName = String(user?.rawRoleName || user?.title || user?.papel_nome || user?.roleName || "").trim().toLowerCase();

  return roleValue === "admin_super"
    || rawRoleCode === "admin_super"
    || rawRoleCode === "admin super"
    || rawRoleName === "administrador super"
    || rawRoleName === "admin super";
}

// Afiliado é um utilizador sem empresa/loja (nunca é staff de nenhuma
// empresa do ERP) — precisa do mesmo tipo de contorno que admin_super tem
// para não cair nos fluxos de onboarding/loja pensados para papéis normais.
function isAfiliadoUser(user) {
  const roleValue = String(user?.roleValue || "").trim().toLowerCase();
  const rawRoleCode = String(user?.rawRoleCode || user?.papel_codigo || "").trim().toLowerCase();
  return roleValue === "afiliado" || rawRoleCode === "afiliado";
}

function AdminSuperOnlyRoute({ children }) {
  const location = useLocation();
  const { isAuthenticated, currentUser } = useErpContext();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location, fromSplash: true }} />;
  }

  if (!isAdminSuperUser(currentUser)) {
    const nextPath = resolvePostLoginPath(currentUser, [], "");
    return <Navigate to={nextPath.path} replace state={nextPath.state || undefined} />;
  }

  return children;
}

// Espelha AdminSuperOnlyRoute: qualquer utilizador autenticado que NÃO seja
// afiliado é reencaminhado para a sua própria rota inicial em vez de ver o
// portal do afiliado — o portal do afiliado nunca deve ficar acessível a
// staff normal do ERP, mesmo por engano/URL directo.
function AfiliadoOnlyRoute({ children }) {
  const location = useLocation();
  const { isAuthenticated, currentUser } = useErpContext();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location, fromSplash: true }} />;
  }

  if (!isAfiliadoUser(currentUser)) {
    const nextPath = resolvePostLoginPath(currentUser, [], "");
    return <Navigate to={nextPath.path} replace state={nextPath.state || undefined} />;
  }

  return children;
}

function HomeRoute() {
  const { currentUser } = useErpContext();
  return <Navigate to={resolveRoleHomePath(currentUser?.roleValue, "", currentUser)} replace />;
}

function LegacyRetificarRoute() {
  const location = useLocation();
  const match = location?.pathname?.match(/\/transacoes\/retificar\/(.+)$/i);
  const uuidVenda = match?.[1] || "";
  return <Navigate to={`/transacoes/ajustes/${uuidVenda}?acao=retificar`} replace />;
}

function LegacyCancelarRoute() {
  const location = useLocation();
  const match = location?.pathname?.match(/\/transacoes\/cancelar\/(.+)$/i);
  const uuidVenda = match?.[1] || "";
  return <Navigate to={`/transacoes/ajustes/${uuidVenda}?acao=cancelar`} replace />;
}

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <ScrollToTop />
      <Suspense fallback={<RouteFallback />}>
        <RouteSeoGuard />
        <AuthSessionGuard />
        <StartupSplashGate />
        <RouterRoutes>
          <Route path="/landing" element={<LandingPage />} />
          <Route path="/verificar/:uuid" element={<VerificarDocumentoPage />} />
          <Route path="/splash" element={<PublicSplashRoute />} />
          <Route path="/login" element={<PublicLoginRoute />} />
          <Route path="/alterar-senha" element={<ProtectedRoute><ChangePasswordPage /></ProtectedRoute>} />
          <Route path="/register" element={<AdminSuperOnlyRoute><RegisterPage /></AdminSuperOnlyRoute>} />
          <Route path="/company-setup" element={<ProtectedRoute><CompanySetupPage /></ProtectedRoute>} />
          <Route path="/subscription-plans" element={<ProtectedRoute><SubscriptionPlansPage /></ProtectedRoute>} />
          <Route path="/admin-painel" element={<AdminSuperOnlyRoute><AdminPanelPage /></AdminSuperOnlyRoute>} />
          <Route path="/admin-licencas-assinatura" element={<ProtectedRoute><AdminSignatureLicensesPage /></ProtectedRoute>} />
          <Route path="/afiliado" element={<AfiliadoOnlyRoute><AfiliadoDashboardPage /></AfiliadoOnlyRoute>} />
          <Route path="/afiliado/vendas" element={<AfiliadoOnlyRoute><AfiliadoVendasPage /></AfiliadoOnlyRoute>} />
          <Route path="/afiliado/vendas/:uuid" element={<AfiliadoOnlyRoute><AfiliadoVendaDetalhePage /></AfiliadoOnlyRoute>} />
          <Route path="/afiliado/comissoes" element={<AfiliadoOnlyRoute><AfiliadoComissoesPage /></AfiliadoOnlyRoute>} />
          <Route path="/afiliado/carteira" element={<AfiliadoOnlyRoute><AfiliadoCarteiraPage /></AfiliadoOnlyRoute>} />
          <Route path="/afiliado/carteira/extrato" element={<AfiliadoOnlyRoute><AfiliadoExtratoPage /></AfiliadoOnlyRoute>} />
          <Route path="/afiliado/saque" element={<AfiliadoOnlyRoute><AfiliadoSolicitarSaquePage /></AfiliadoOnlyRoute>} />
          <Route path="/afiliado/saques" element={<AfiliadoOnlyRoute><AfiliadoSaquesPage /></AfiliadoOnlyRoute>} />
          <Route path="/afiliado/saques/:uuid" element={<AfiliadoOnlyRoute><AfiliadoSaqueDetalhePage /></AfiliadoOnlyRoute>} />
          <Route path="/afiliado/pagamento" element={<AfiliadoOnlyRoute><AfiliadoPagamentoPage /></AfiliadoOnlyRoute>} />
          <Route path="/afiliado/perfil" element={<AfiliadoOnlyRoute><AfiliadoPerfilPage /></AfiliadoOnlyRoute>} />
          <Route path="/company-registration" element={<Navigate to="/company-setup" replace />} />
          <Route path="/" element={<ProtectedRoute><HomeRoute /></ProtectedRoute>} />
          <Route path="/manager-overview" element={<ProtectedRoute><ManagerOverview /></ProtectedRoute>} />
          <Route path="/ceo-dashboard" element={<ProtectedRoute><CeoDashboard /></ProtectedRoute>} />
          <Route path="/store-manager-dashboard" element={<ProtectedRoute><StoreManagerDashboard /></ProtectedRoute>} />
          <Route path="/executive-kpi-dashboard" element={<ProtectedRoute><ExecutiveKpiDashboard /></ProtectedRoute>} />
          <Route path="/financial-compliance-hub" element={<ProtectedRoute><FinancialComplianceHub /></ProtectedRoute>} />
          <Route path="/treasury-dashboard" element={<ProtectedRoute><TreasuryDashboardPage /></ProtectedRoute>} />
          <Route path="/agt-management" element={<ProtectedRoute><AgtManagement /></ProtectedRoute>} />
          <Route path="/sales-analytics-dashboard" element={<ProtectedRoute><SalesAnalyticsDashboard /></ProtectedRoute>} />
          <Route path="/pos-terminal" element={<ProtectedRoute><PosTerminal /></ProtectedRoute>} />
          <Route path="/pos-terminal/sucesso" element={<ProtectedRoute><PosSaleSuccess /></ProtectedRoute>} />
          <Route path="/customer-registry" element={<ProtectedRoute><CustomerRegistry /></ProtectedRoute>} />
          <Route path="/product-catalog" element={<ProtectedRoute><ProductCatalog /></ProtectedRoute>} />
          <Route path="/transacoes" element={<ProtectedRoute><Transacoes /></ProtectedRoute>} />
          <Route path="/transacoes/ajustes/:uuidVenda" element={<ProtectedRoute><TransacoesAjustes /></ProtectedRoute>} />
          <Route path="/transacoes/validar/:uuidVenda" element={<ProtectedRoute><TransacoesValidar /></ProtectedRoute>} />
          <Route path="/transacoes/documentos/:uuidVenda" element={<ProtectedRoute><TransacoesDocumentos /></ProtectedRoute>} />
          <Route path="/transacoes/documentos-lista/:uuidVenda" element={<ProtectedRoute><TransacoesDocumentosLista /></ProtectedRoute>} />
          <Route path="/produtos-vendidos" element={<ProtectedRoute><ProdutosVendidosPage /></ProtectedRoute>} />
          <Route path="/observability-events" element={<AdminSuperOnlyRoute><ObservabilityEventsPage /></AdminSuperOnlyRoute>} />
          <Route path="/assistant-telemetry" element={<AdminSuperOnlyRoute><AssistantTelemetryPage /></AdminSuperOnlyRoute>} />
          <Route path="/cobrancas" element={<ProtectedRoute><CobrancasPage /></ProtectedRoute>} />
          <Route path="/vendor-management" element={<ProtectedRoute><VendorManagementPage /></ProtectedRoute>} />
          <Route path="/pos-relatorio-diario" element={<ProtectedRoute><PosDailyReportPage /></ProtectedRoute>} />
          <Route path="/access-control" element={<ProtectedRoute><AccessControlPage /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
          <Route path="/home-splash" element={<ProtectedRoute><SplashHomePage /></ProtectedRoute>} />
          <Route path="/profile-settings" element={<ProtectedRoute><ProfileSettingsPage /></ProtectedRoute>} />
          <Route path="/ajuda-suporte" element={<ProtectedRoute><AjudaSuportePage /></ProtectedRoute>} />
          <Route path="/transacoes/retificar/:uuidVenda" element={<ProtectedRoute><LegacyRetificarRoute /></ProtectedRoute>} />
          <Route path="/transacoes/cancelar/:uuidVenda" element={<ProtectedRoute><LegacyCancelarRoute /></ProtectedRoute>} />
          <Route path="*" element={<GuardedNotFoundRoute />} />
        </RouterRoutes>
      </Suspense>
      <FloatingChatbot />
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
